import Column from '@app/components/layout/Column'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { CreationStepLayout } from '../blocks/CreationStepLayout'
import { useEffect, useMemo, useState } from 'react'
import { useInitiativeAutoSave } from '../../shared/useInitiativeAutoSave'
import { useTranslations } from '@i18n/useLanguage'
import { enrich } from '@i18n/localizeHelper'
import { Style } from 'inlines'
import type { InitiativeWithId } from '../../shared/initiativeTypes'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { PHASE_TYPES, PhaseType } from '@schema/phase'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { normalizePhoneInput } from '@app/util/helpers'
import {
  createContactDetailsSchema,
  type ContactDetailsFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import { InitiativeAddressInput } from '../blocks/InitiativeAddressInput'
import {
  createInitiativeAddressState,
  parsePlotAddress,
} from '../blocks/initiativeAddressState'
import { PLOT_STATUS, type Initiative } from '@schema/initiative'

const CURRENT_STEP = initiativeCreationSteps.contact

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

type ContactField = keyof ContactDetailsFormValues

const emptyTouched = (): Record<ContactField, boolean> => ({
  contactFirstName: false,
  contactLastName: false,
  contactEmail: false,
  contactPhoneNumber: false,
})

export const ContactStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const formValidation = useTranslations().app.form.validation
  const { user } = useUserContext()

  const [contactFirstName, setContactFirstName] = useState(
    () => initiative.contactFirstName ?? '',
  )
  const [contactLastName, setContactLastName] = useState(
    () => initiative.contactLastName ?? '',
  )
  const [contactEmail, setContactEmail] = useState(
    () => initiative.contactEmail ?? '',
  )
  const [contactPhoneNumber, setContactPhoneNumber] = useState(() =>
    normalizePhoneInput(initiative.contactPhoneNumber ?? ''),
  )
  const [touched, setTouched] = useState(emptyTouched)
  const [addressState, setAddressState] = useState(() =>
    createInitiativeAddressState(initiative),
  )
  const { address, resolvedAddress, geoCoords, resolvedAddressRefs } =
    addressState
  const isLivingPhase = selectedPhaseType === PHASE_TYPES.living

  useEffect(() => {
    if (!user) {
      return
    }

    setContactFirstName((current) =>
      current.trim() === '' ? (user.firstName ?? '') : current,
    )
    setContactLastName((current) =>
      current.trim() === '' ? (user.lastName ?? '') : current,
    )
    setContactEmail((current) =>
      current.trim() === '' ? (user.email ?? '') : current,
    )
  }, [user])

  const schema = useMemo(
    () =>
      createContactDetailsSchema({
        required: formValidation.required,
        invalidEmail: formValidation.invalidEmail,
        phoneMinLength: enrich(formValidation.minLength, 9),
      }),
    [formValidation],
  )

  const formValues = useMemo(
    (): ContactDetailsFormValues => ({
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhoneNumber,
    }),
    [contactFirstName, contactLastName, contactEmail, contactPhoneNumber],
  )

  const fieldErrors = useMemo((): Partial<Record<ContactField, string>> => {
    const result = schema.safeParse(formValues)
    if (result.success) {
      return {}
    }

    const errors: Partial<Record<ContactField, string>> = {}
    for (const issue of result.error.issues) {
      const field = issue.path[0]
      if (typeof field === 'string' && !(field in errors)) {
        errors[field as ContactField] = issue.message
      }
    }
    return errors
  }, [schema, formValues])

  const isValid = Object.keys(fieldErrors).length === 0

  const showError = (field: ContactField): string | undefined =>
    touched[field] ? fieldErrors[field] : undefined

  const markTouched = (field: ContactField) => {
    setTouched((current) => ({ ...current, [field]: true }))
  }

  const savePayload = useMemo((): Partial<Initiative> => {
    const contactFields = {
      contactFirstName: contactFirstName.trim(),
      contactLastName: contactLastName.trim(),
      contactEmail: contactEmail.trim(),
      contactPhoneNumber,
      onboardingStep: CURRENT_STEP,
    }

    if (!isLivingPhase) {
      return contactFields
    }

    const trimmedAddress = address.trim()
    const parsed = parsePlotAddress(trimmedAddress)

    return {
      ...contactFields,
      permit: true,
      potentialPlotStatus: PLOT_STATUS.underOption,
      potentialPlotAddress: resolvedAddress || trimmedAddress,
      location: {
        address: {
          postalCode: parsed.postalCode,
          street: parsed.street || trimmedAddress || null,
          region: resolvedAddressRefs.region,
          locality: resolvedAddressRefs.locality,
          municipality: resolvedAddressRefs.municipality,
          country: resolvedAddressRefs.country,
        },
        geo: geoCoords ?? undefined,
      },
    }
  }, [
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhoneNumber,
    isLivingPhase,
    address,
    resolvedAddress,
    geoCoords,
    resolvedAddressRefs,
  ])

  useInitiativeAutoSave(initiative.id, savePayload)

  useEffect(() => {
    if (isValid) {
      completeStep(CURRENT_STEP)
      return
    }

    uncompleteStep(CURRENT_STEP)
  }, [isValid, completeStep, uncompleteStep])

  return (
    <CreationStepLayout
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      {selectedPhaseType === PHASE_TYPES.living && (
        <InitiativeAddressInput
          state={addressState}
          onStateChange={setAddressState}
          labelStyle={labelStyle}
        />
      )}
      <Column gap="1rem" alignItems="stretch">
        <TextInput
          label={translations.contactPersonFirstName}
          labelStyle={labelStyle}
          required={true}
          text={contactFirstName}
          setText={setContactFirstName}
          onBlur={() => markTouched('contactFirstName')}
          errorMessage={showError('contactFirstName')}
        />
        <TextInput
          label={translations.contactPersonLastName}
          labelStyle={labelStyle}
          required={true}
          text={contactLastName}
          setText={setContactLastName}
          onBlur={() => markTouched('contactLastName')}
          errorMessage={showError('contactLastName')}
        />
        <TextInput
          label={translations.contactPersonEmailAdress}
          labelStyle={labelStyle}
          required={true}
          text={contactEmail}
          setText={setContactEmail}
          inputType="email"
          inputMode="email"
          onBlur={() => markTouched('contactEmail')}
          errorMessage={showError('contactEmail')}
        />
        <TextInput
          label={translations.contactPersonPhoneNumber}
          labelStyle={labelStyle}
          required={true}
          text={contactPhoneNumber}
          setText={(value) => setContactPhoneNumber(normalizePhoneInput(value))}
          placeholder={translations.contactPhonePlaceholder}
          inputType="tel"
          inputMode="tel"
          maxLength={16}
          onBlur={() => markTouched('contactPhoneNumber')}
          errorMessage={showError('contactPhoneNumber')}
        />
      </Column>
    </CreationStepLayout>
  )
}
